(() => {
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let scrollGradientFrame = 0;
  let scrollGradientBound = false;

  const paintScrollGradients = () => {
    scrollGradientFrame = 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
    document.querySelectorAll('.jj-value-inspection__heading-accent, [data-jj-scroll-gradient]').forEach((accent) => {
      if (prefersReducedMotion()) {
        accent.style.setProperty('--jj-gradient-shift', '32%');
        return;
      }
      const section = accent.closest('.jj-paid-value--inspection') || accent;
      const rect = section.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (viewportHeight - rect.top) / (viewportHeight + rect.height)));
      accent.style.setProperty('--jj-gradient-shift', `${Math.round(8 + (progress * 92))}%`);
    });
  };

  const queueScrollGradientPaint = () => {
    if (scrollGradientFrame) return;
    scrollGradientFrame = window.requestAnimationFrame(paintScrollGradients);
  };

  const initScrollGradients = () => {
    if (!scrollGradientBound) {
      scrollGradientBound = true;
      window.addEventListener('scroll', queueScrollGradientPaint, { passive: true });
      window.addEventListener('resize', queueScrollGradientPaint, { passive: true });
    }
    queueScrollGradientPaint();
  };

  const initSmoothAnchors = (root) => {
    root.querySelectorAll('.jj-paid a[href^="#"]').forEach((link) => {
      if (link.dataset.jjSmoothBound === 'true') return;
      const hash = link.getAttribute('href');
      if (!hash || hash === '#') return;
      const target = document.getElementById(hash.slice(1));
      if (!target) return;
      link.dataset.jjSmoothBound = 'true';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        target.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'start'
        });
        if (window.location.hash !== hash) {
          try {
            window.history.pushState(null, '', hash);
          } catch (error) {
            return;
          }
        }
      });
    });
  };

  const initGallery = (gallery) => {
    if (!gallery || gallery.dataset.jjReady === 'true') return;
    gallery.dataset.jjReady = 'true';
    const viewport = gallery.querySelector('.jj-paid-gallery__viewport');
    const getSlides = () => Array.from(gallery.querySelectorAll('[data-jj-slide]'));
    const getThumbs = () => Array.from(gallery.querySelectorAll('[data-jj-thumb]'));
    const defaultSlides = getSlides();
    const defaultThumbs = getThumbs();
    const count = gallery.querySelector('[data-jj-count]');
    let current = 0;
    const offerSlide = defaultSlides[0];
    const offerThumb = defaultThumbs[0];
    const slideDescriptions = new WeakMap(defaultSlides.map((slide) => {
      const label = slide.getAttribute('aria-label') || '';
      return [slide, label.replace(/^\d+\s+of\s+\d+(?::\s*)?/, '')];
    }));
    let variantSlide = null;
    let variantThumb = null;

    const setImageAttribute = (image, name, value) => {
      if (!image) return;
      if (value) image.setAttribute(name, value);
      else image.removeAttribute(name);
    };

    const refreshGalleryMetadata = () => {
      const slides = getSlides();
      const thumbs = getThumbs();
      slides.forEach((slide, index) => {
        const description = slideDescriptions.get(slide) || '';
        slide.setAttribute('aria-label', `${index + 1} of ${slides.length}${description ? `: ${description}` : ''}`);
      });
      thumbs.forEach((thumb, index) => {
        thumb.dataset.jjThumb = String(index);
        thumb.setAttribute('aria-label', thumb === variantThumb ? `Show ${variantThumb.dataset.jjVariantTitle} product image` : `Show image ${index + 1}`);
      });
    };

    const removeVariantSlide = () => {
      variantSlide?.remove();
      variantThumb?.remove();
      variantSlide = null;
      variantThumb = null;
      refreshGalleryMetadata();
    };

    const updateVariantSlide = (detail = {}) => {
      const useDefault = detail.useGalleryDefault === true || detail.useGalleryDefault === 'true' || !detail.image;
      if (useDefault || !offerSlide || !offerThumb || !viewport) {
        removeVariantSlide();
        return;
      }

      if (!variantSlide) {
        variantSlide = offerSlide.cloneNode(true);
        variantSlide.removeAttribute('id');
        variantSlide.removeAttribute('data-shopify-editor-block');
        variantSlide.setAttribute('data-jj-variant-slide', '');
        viewport.insertBefore(variantSlide, offerSlide);
      }
      if (!variantThumb) {
        variantThumb = offerThumb.cloneNode(true);
        variantThumb.removeAttribute('id');
        variantThumb.setAttribute('data-jj-variant-thumb', '');
        offerThumb.parentNode?.insertBefore(variantThumb, offerThumb);
      }

      const title = detail.title || 'Selected color';
      const imageAlt = detail.imageAlt || `${title} Tomorrow's Starter Kit`;
      const image = variantSlide.querySelector('img');
      const thumbnail = variantThumb.querySelector('img');
      if (image) {
        image.setAttribute('src', detail.image);
        setImageAttribute(image, 'srcset', '');
        setImageAttribute(image, 'sizes', '');
        image.setAttribute('alt', imageAlt);
        image.setAttribute('loading', 'eager');
        image.setAttribute('fetchpriority', 'high');
      }
      if (thumbnail) {
        thumbnail.setAttribute('src', detail.thumbnail || detail.image);
        setImageAttribute(thumbnail, 'srcset', '');
        setImageAttribute(thumbnail, 'sizes', '');
        thumbnail.setAttribute('alt', '');
      }
      variantThumb.dataset.jjVariantTitle = title;
      slideDescriptions.set(variantSlide, `${title} Ceramic Training Bottle and NRGStripz, together for $56.`);
      refreshGalleryMetadata();
    };

    const setCurrent = (index, move) => {
      const slides = getSlides();
      const thumbs = getThumbs();
      if (!slides.length) return;
      current = (index + slides.length) % slides.length;
      thumbs.forEach((thumb, thumbIndex) => thumb.setAttribute('aria-current', thumbIndex === current ? 'true' : 'false'));
      if (count) count.textContent = `${current + 1} / ${slides.length}`;
      if (move && viewport) viewport.scrollTo({ left: slides[current].offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };

    document.addEventListener('jj:variant-change', (event) => {
      updateVariantSlide(event.detail || {});
      setCurrent(0, true);
    });

    gallery.querySelector('[data-jj-prev]')?.addEventListener('click', () => setCurrent(current - 1, true));
    gallery.querySelector('[data-jj-next]')?.addEventListener('click', () => setCurrent(current + 1, true));
    gallery.querySelector('.jj-paid-gallery__thumbs')?.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-jj-thumb]');
      if (!thumb || !gallery.contains(thumb)) return;
      setCurrent(getThumbs().indexOf(thumb), true);
    });

    if (viewport && defaultSlides.length > 1) {
      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          setCurrent(current - 1, true);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          setCurrent(current + 1, true);
        }
      });
      let scrollFrame = 0;
      viewport.addEventListener('scroll', () => {
        if (scrollFrame) return;
        scrollFrame = requestAnimationFrame(() => {
          const width = viewport.clientWidth || 1;
          setCurrent(Math.round(viewport.scrollLeft / width), false);
          scrollFrame = 0;
        });
      }, { passive: true });
    }
  };

  const bindVariantPickers = () => {
    const options = Array.from(document.querySelectorAll('[data-jj-variant-option]'));
    const variantInputs = Array.from(document.querySelectorAll('[data-jj-variant-input]'));
    const selectedLabels = Array.from(document.querySelectorAll('[data-jj-selected-variant]'));

    const updateButtons = (available) => {
      variantInputs.forEach((input) => {
        const form = input.closest('form');
        const button = form && form.querySelector('button[name="add"]');
        const label = button && button.querySelector('.btn-text');
        if (!button || !label) return;
        if (!button.dataset.jjAvailableLabel) button.dataset.jjAvailableLabel = label.textContent.trim();
        button.disabled = !available;
        if (available) {
          button.removeAttribute('aria-disabled');
        } else {
          button.setAttribute('aria-disabled', 'true');
        }
        label.textContent = available ? button.dataset.jjAvailableLabel : 'Sold out';
      });
    };

    const selectVariant = (id, title, available) => {
      options.forEach((option) => {
        option.checked = option.value === id;
      });
      variantInputs.forEach((input) => {
        input.value = id;
      });
      selectedLabels.forEach((label) => {
        label.textContent = title;
      });
      updateButtons(available);
      const selectedOption = options.find((option) => option.value === id);
      document.dispatchEvent(new CustomEvent('jj:variant-change', {
        detail: {
          id,
          title,
          available,
          image: selectedOption?.dataset.jjVariantImage || '',
          thumbnail: selectedOption?.dataset.jjVariantThumbnail || '',
          imageAlt: selectedOption?.dataset.jjVariantImageAlt || '',
          useGalleryDefault: selectedOption?.dataset.jjUseGalleryDefault === 'true'
        }
      }));
    };

    options.forEach((option) => {
      if (option.dataset.jjBound === 'true') return;
      option.dataset.jjBound = 'true';
      option.addEventListener('change', () => {
        if (!option.checked) return;
        selectVariant(option.value, option.dataset.jjVariantTitle || 'Selected color', option.dataset.jjVariantAvailable !== 'false');
      });
    });

    const initial = options.find((option) => option.checked && !option.disabled) || options.find((option) => !option.disabled);
    if (initial) selectVariant(initial.value, initial.dataset.jjVariantTitle || 'Selected color', initial.dataset.jjVariantAvailable !== 'false');
  };

  const initValueExplorer = (explorer) => {
    if (!explorer || explorer.dataset.jjReady === 'true') return;
    explorer.dataset.jjReady = 'true';
    const markers = Array.from(explorer.querySelectorAll('[data-jj-value-marker]'));
    const tray = explorer.querySelector('[data-jj-value-tray]');
    const label = tray?.querySelector('[data-jj-value-label]');
    const heading = tray?.querySelector('[data-jj-value-heading]');
    const body = tray?.querySelector('[data-jj-value-body]');
    let swapTimer = 0;

    const updateTray = (marker) => {
      if (label) label.textContent = marker.dataset.jjLabel || '';
      if (heading) heading.textContent = marker.dataset.jjHeading || '';
      if (body) body.textContent = marker.dataset.jjBody || '';
    };

    const selectMarker = (marker) => {
      if (!marker || marker.getAttribute('aria-pressed') === 'true') return;
      markers.forEach((item) => item.setAttribute('aria-pressed', item === marker ? 'true' : 'false'));
      window.clearTimeout(swapTimer);
      if (!tray || prefersReducedMotion()) {
        updateTray(marker);
        return;
      }
      tray.classList.add('is-updating');
      swapTimer = window.setTimeout(() => {
        updateTray(marker);
        requestAnimationFrame(() => tray.classList.remove('is-updating'));
      }, 150);
    };

    markers.forEach((marker) => marker.addEventListener('click', () => selectMarker(marker)));
    if (markers.length) {
      updateTray(markers.find((marker) => marker.getAttribute('aria-pressed') === 'true') || markers[0]);
      tray?.classList.remove('is-updating');
    }

    if (!prefersReducedMotion() && 'IntersectionObserver' in window && markers.length) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        markers.forEach((marker) => marker.classList.add('is-pulsing'));
        window.setTimeout(() => markers.forEach((marker) => marker.classList.remove('is-pulsing')), 3400);
        observer.disconnect();
      }, { threshold: 0.42 });
      observer.observe(explorer);
    }
  };

  const initFaqItem = (details) => {
    if (!details || details.dataset.jjReady === 'true' || prefersReducedMotion()) return;
    const summary = details.querySelector('summary');
    const content = details.querySelector('[data-jj-faq-content]');
    if (!summary || !content) return;
    details.dataset.jjReady = 'true';
    details.dataset.jjFaqReady = 'true';
    content.style.height = details.open ? 'auto' : '0px';
    content.style.opacity = details.open ? '1' : '0';

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (details.dataset.jjAnimating === 'true') return;
      details.dataset.jjAnimating = 'true';
      const opening = !details.open;
      let finished = false;
      let fallbackTimer = 0;

      const finish = () => {
        if (finished) return;
        finished = true;
        window.clearTimeout(fallbackTimer);
        details.dataset.jjAnimating = 'false';
        if (opening) {
          content.style.height = 'auto';
          content.style.opacity = '1';
        } else {
          details.open = false;
          content.style.height = '0px';
          content.style.opacity = '0';
        }
      };

      const onTransitionEnd = (transitionEvent) => {
        if (transitionEvent.propertyName !== 'height') return;
        content.removeEventListener('transitionend', onTransitionEnd);
        finish();
      };

      content.addEventListener('transitionend', onTransitionEnd);
      fallbackTimer = window.setTimeout(finish, 420);

      if (opening) {
        details.open = true;
        content.style.height = '0px';
        content.style.opacity = '0';
        requestAnimationFrame(() => {
          content.style.height = `${content.scrollHeight}px`;
          content.style.opacity = '1';
        });
      } else {
        content.style.height = `${content.scrollHeight}px`;
        content.style.opacity = '1';
        requestAnimationFrame(() => {
          content.style.height = '0px';
          content.style.opacity = '0';
        });
      }
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-jj-gallery]').forEach(initGallery);
    root.querySelectorAll('[data-jj-value-explorer]').forEach(initValueExplorer);
    root.querySelectorAll('[data-jj-faq-item]').forEach(initFaqItem);
    initSmoothAnchors(root);
    initScrollGradients();
    bindVariantPickers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
